import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_SURVEYS } from '@/api/endpoints';
import { SURVEY_QUERY_KEY, SURVEYS_QUERY_KEY } from '@/api/queryKeys';
import { markSurveyFinishedLocally } from '@/pages/Mail/surveys';
import { SurveyUserStatus } from '@/types/components';
import type { SurveyResponse } from '@/types/survey';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { joinPath } from '@/utils/global';

interface Params {
  surveyId: string;
  onSuccess: () => void;
  onError: () => void;
}

export const useFinishSurvey = ({ surveyId, onSuccess, onError }: Params) => {
  const queryClient = useQueryClient();

  const { mutate: finishSurvey, isPending: isFinishing } = useMutation({
    mutationFn: () =>
      apiClient.post(`${joinPath(API_SURVEYS, surveyId)}/finish`),
    onSuccess: () => {
      markSurveyFinishedLocally(surveyId);
      queryClient.setQueryData<SurveyResponse[]>(SURVEYS_QUERY_KEY, (surveys) =>
        surveys?.map((survey) =>
          survey.id === surveyId
            ? { ...survey, currentUserStatus: SurveyUserStatus.Finished }
            : survey,
        ),
      );
      queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEY, surveyId],
      });
      queryClient.invalidateQueries({ queryKey: SURVEYS_QUERY_KEY });
      onSuccess();
    },
    onError: (error) => {
      logRequestError(ErrorMessage.FinishSurvey, error);
      onError();
    },
  });

  return { finishSurvey, isFinishing };
};
